import {
  AUTH_PRINCIPAL_RESOLVE_CHANNEL,
  EMAIL_SOURCE_READ,
  authPrincipalResolveResponseSchema,
  createExternalActorId,
  emailSourceReadRequestSchema,
  type EmailSourceReadRequest,
  type EmailSourceReadResponse,
  type InboundEmailSender,
} from "@brains/contracts";
import {
  MessageInterfacePlugin,
  type ChannelDeliveryInput,
  type Daemon,
  type IRuntimeStateStore,
  type MessageInterfacePluginContext,
} from "@brains/plugins";
import { getErrorMessage } from "@brains/utils/error";
import { type FetchLike } from "@brains/utils/fetch-like";
import { z } from "@brains/utils/zod";
import packageJson from "../package.json";
import {
  createInboundEmailClient,
  intakeInboundEmail,
  emailImapConfigSchema,
  type InboundEmailClientFactory,
  type InboundEmailCursor,
} from "./inbound-email";
import {
  InboundEmailSupervisor,
  type InboundEmailSleep,
} from "./inbound-supervisor";
import {
  EmailSourceLocatorStore,
  emailSourceLocatorSchema,
} from "./source-locator-store";
import { readEmailSource } from "./source-reader";

export {
  EMAIL_INBOUND,
  inboundEmailSchema,
  type InboundEmail,
  type InboundEmailAddress,
  type InboundEmailSender,
} from "@brains/contracts";
export type {
  EmailImapConfig,
  EmailImapConfigInput,
  InboundEmailClient,
  InboundEmailClientFactory,
  InboundEmailCursor,
  InboundEmailSelection,
  InboundEmailSourceMessage,
} from "./inbound-email";
export { createInboundEmailSourceRef } from "./inbound-email";
export type { InboundEmailSleep } from "./inbound-supervisor";

// Unset env vars interpolate to empty strings in brain.yaml; an optional
// outbound setting left empty means "absent", not invalid — inbound-only
// postures must still boot.
const optionalConfigString: z.ZodOptional<
  z.ZodUnion<
    [z.ZodPipe<z.ZodString, z.ZodTransform<undefined, string>>, z.ZodString]
  >
> = z
  .string()
  .max(0)
  .transform((): undefined => undefined)
  .or(z.string().min(1))
  .optional();

const emailConfigSchema: z.ZodObject<{
  transport: z.ZodDefault<z.ZodLiteral<"resend">>;
  apiKey: typeof optionalConfigString;
  from: typeof optionalConfigString;
  imap: z.ZodOptional<typeof emailImapConfigSchema>;
}> = z.object({
  transport: z.literal("resend").default("resend"),
  apiKey: optionalConfigString,
  from: optionalConfigString,
  imap: emailImapConfigSchema.optional(),
});

export type EmailConfig = z.output<typeof emailConfigSchema>;
export type EmailConfigInput = z.input<typeof emailConfigSchema>;

const resendEmailResponseSchema: z.ZodObject<
  { id: z.ZodOptional<z.ZodString> },
  z.core.$loose
> = z.looseObject({
  id: z.string().trim().min(1).max(1_000).optional(),
});

type EmailDeliveryThreading = NonNullable<ChannelDeliveryInput["threading"]>;
const emailMessageIdSchema: z.ZodString = z
  .string()
  .trim()
  .min(1)
  .max(2_000)
  .refine((value) => !/[\p{Cc}\p{Cf}]/u.test(value), {
    message: "Email threading identifiers cannot contain controls",
  });
const emailDeliveryThreadingSchema: z.ZodObject<
  {
    inReplyTo: z.ZodString;
    references: z.ZodArray<z.ZodString>;
  },
  z.core.$strict
> = z.strictObject({
  inReplyTo: emailMessageIdSchema,
  references: z.array(emailMessageIdSchema).max(100),
});

function expectEmailDeliveryThreading(
  value: z.output<typeof emailDeliveryThreadingSchema>,
): EmailDeliveryThreading {
  return value;
}
void expectEmailDeliveryThreading;

function expectEmailDeliveryThreadingInput(
  value: EmailDeliveryThreading,
): z.input<typeof emailDeliveryThreadingSchema> {
  return value;
}
void expectEmailDeliveryThreadingInput;

export type EmailSendResult =
  { status: "sent"; id?: string } | { status: "failed"; code: string };

/** Resend's error name only: its message can name addresses or domains. */
const resendErrorSchema = z.object({
  name: z.string().regex(/^[a-z_]{1,64}$/),
});

async function resendFailureCode(response: Response): Promise<string> {
  const body: unknown = await response.json().catch((): undefined => {
    // A body that is not JSON still has its status.
    return undefined;
  });
  const parsed = resendErrorSchema.safeParse(body);
  return parsed.success
    ? `resend_${parsed.data.name}`
    : `resend_http_${response.status}`;
}

/**
 * Whether a failed delivery must keep recipient and subject out of the logs.
 *
 * Only an explicit "normal" opts out. A caller that never considered
 * sensitivity gets the safe treatment rather than leaking an address.
 */
export function shouldRedactDelivery(
  sensitivity: ChannelDeliveryInput["sensitivity"],
): boolean {
  return sensitivity !== "normal";
}

export interface EmailInterfaceDependencies {
  fetchImpl?: FetchLike;
  imapClientFactory?: InboundEmailClientFactory;
  inboundSleep?: InboundEmailSleep;
}

/** Email message interface with Resend delivery and optional IMAP intake. */
export class EmailInterface extends MessageInterfacePlugin<
  EmailConfig,
  EmailConfigInput
> {
  private readonly fetchImpl: FetchLike;
  private readonly imapClientFactory: InboundEmailClientFactory;
  private readonly inboundSleep: InboundEmailSleep | undefined;
  private inboundCursor?: IRuntimeStateStore<InboundEmailCursor>;
  private sourceLocators?: EmailSourceLocatorStore;

  constructor(
    config: EmailConfigInput = {},
    dependencies: EmailInterfaceDependencies = {},
  ) {
    super("email", packageJson, config, emailConfigSchema);
    this.fetchImpl = dependencies.fetchImpl ?? fetch;
    this.imapClientFactory =
      dependencies.imapClientFactory ?? createInboundEmailClient;
    this.inboundSleep = dependencies.inboundSleep;
  }

  protected override createDaemon(): Daemon | undefined {
    const config = this.config.imap;
    if (!config) return undefined;

    const supervisor = new InboundEmailSupervisor({
      config,
      createClient: this.imapClientFactory,
      intake: async (client, selection): Promise<number> =>
        intakeInboundEmail(client, selection, {
          cursor: this.getInboundCursor(),
          publish: this.getContext().messaging.send,
          resolveSender: async (
            address,
          ): Promise<InboundEmailSender | undefined> =>
            this.resolveInboundSender(address),
          recordSourceLocator: async (sourceRef, selection, uid) =>
            this.getSourceLocators().record(sourceRef, selection, uid),
          pruneSourceLocators: async () => this.getSourceLocators().prune(),
          logger: this.logger,
        }),
      logger: this.logger,
      ...(this.inboundSleep ? { sleep: this.inboundSleep } : {}),
    });

    return {
      start: async (): Promise<void> => {
        try {
          await supervisor.start();
          this.logger.info(
            supervisor.isConnected()
              ? "Inbound email listener connected"
              : "Inbound email listener started; awaiting connection",
          );
        } catch {
          throw new Error("Inbound email listener failed to start");
        }
      },
      stop: async (): Promise<void> => {
        try {
          await supervisor.stop();
          this.logger.info("Inbound email listener disconnected");
        } catch {
          throw new Error("Inbound email listener failed to disconnect");
        }
      },
      healthCheck: async () => ({
        status: supervisor.isConnected() ? "healthy" : "error",
        message: supervisor.isConnected()
          ? "Inbound email listener connected"
          : supervisor.isRunning()
            ? "Inbound email listener awaiting connection"
            : "Inbound email listener disconnected",
        lastCheck: new Date(),
      }),
    };
  }

  protected override async onRegister(
    context: MessageInterfacePluginContext,
  ): Promise<void> {
    await super.onRegister(context);
    if (this.config.imap) {
      this.inboundCursor = context.runtimeState.scoped({
        namespace: "email.inbound.uid-cursor",
        schema: z.strictObject({
          mailbox: z.string().min(1),
          uidValidity: z.string().regex(/^[1-9]\d*$/),
          lastUid: z.number().int().nonnegative(),
        }),
      });
      this.sourceLocators = new EmailSourceLocatorStore(
        context.runtimeState.scoped({
          namespace: "email.inbound.source-locators",
          schema: emailSourceLocatorSchema,
        }),
      );
      context.messaging.subscribe<EmailSourceReadRequest>(
        EMAIL_SOURCE_READ,
        async (message) => ({
          success: true,
          data: await this.readSource(message.payload),
        }),
      );
    }
  }

  /**
   * The Email channel and its Resend sender. Registered in the worker too, so
   * contact alerts and other background jobs can send email.
   */
  protected override registerChannels(
    context: MessageInterfacePluginContext,
  ): void {
    context.channels.registerDescriptor({
      type: "email",
      displayName: "Email",
      subjectLabel: "Email address",
      subjectPattern: {
        source: "^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$",
        flags: "i",
      },
      manualDelivery: true,
    });

    if (!this.config.apiKey || !this.config.from) {
      this.logger.warn(
        "Email interface transport is disabled because apiKey or from is missing",
      );
      return;
    }

    // The one way to send email. Senders resolve this provider by channel
    // type rather than publishing to a transport-specific channel, so they
    // never need to know the transport exists.
    context.channels.registerDeliveryProvider({
      channelType: "email",
      isAvailable: async () => true,
      send: async (input) => this.deliver(input),
    });
  }

  private async resolveInboundSender(
    address: string,
  ): Promise<InboundEmailSender | undefined> {
    const response = await this.getContext().messaging.send({
      type: AUTH_PRINCIPAL_RESOLVE_CHANNEL,
      payload: {
        actor: {
          kind: "external",
          externalActorId: createExternalActorId(
            "email",
            address.trim().toLowerCase(),
          ),
        },
      },
    });
    if ("noop" in response || !response.success) return undefined;

    const resolution = authPrincipalResolveResponseSchema.safeParse(
      response.data,
    );
    const principal = resolution.success
      ? resolution.data.principal
      : undefined;
    return principal
      ? {
          personId: principal.personId,
          displayName: principal.displayName,
          permissionLevel: principal.permissionLevel,
        }
      : undefined;
  }

  private getInboundCursor(): IRuntimeStateStore<InboundEmailCursor> {
    if (!this.inboundCursor) {
      throw new Error("Inbound email cursor is unavailable");
    }
    return this.inboundCursor;
  }

  private getSourceLocators(): EmailSourceLocatorStore {
    if (!this.sourceLocators) {
      throw new Error("Inbound email source locators are unavailable");
    }
    return this.sourceLocators;
  }

  private async readSource(input: unknown): Promise<EmailSourceReadResponse> {
    const request = emailSourceReadRequestSchema.safeParse(input);
    if (!request.success || request.data.actor.permissionLevel !== "admin") {
      return { kind: "unavailable" };
    }
    const config = this.config.imap;
    if (!config) return { kind: "unavailable" };

    try {
      const locator = await this.getSourceLocators().resolve(
        request.data.sourceRef,
      );
      if (!locator) return { kind: "unavailable" };
      const timeout = AbortSignal.timeout(10_000);
      const signal = request.data.signal
        ? AbortSignal.any([request.data.signal, timeout])
        : timeout;
      // readEmailSource parses its own success path and returns
      // EmailSourceReadResponse, which is z.output of this same schema — so
      // the compiler already guarantees the shape and re-parsing here only
      // re-trims strings that were trimmed one call ago.
      return await readEmailSource(
        config,
        this.imapClientFactory,
        locator,
        signal,
      );
    } catch {
      // One fixed outcome for every failure: a caller must not learn from
      // it whether the message exists or the mailbox refused us.
      return { kind: "unavailable" };
    }
  }

  private async deliver(input: ChannelDeliveryInput): Promise<
    | { status: "sent"; providerDeliveryId?: string }
    | {
        status: "failed";
        failureCode: string;
      }
  > {
    const secret = shouldRedactDelivery(input.sensitivity);

    try {
      const result = await this.sendWithResend({
        to: input.recipient,
        subject: input.subject,
        text: input.text,
        ...(input.html ? { html: input.html } : {}),
        ...(input.threading
          ? { threading: emailDeliveryThreadingSchema.parse(input.threading) }
          : {}),
        idempotencyKey: input.idempotencyKey,
      });
      if (result.status === "sent")
        return {
          status: "sent" as const,
          ...(result.id ? { providerDeliveryId: result.id } : {}),
        };
      this.logger.warn(
        secret
          ? "Email delivery failed for a secret message"
          : "Email delivery failed",
        {
          failureCode: result.code,
          ...(secret ? {} : { to: input.recipient, subject: input.subject }),
        },
      );
      return { status: "failed" as const, failureCode: result.code };
    } catch (error) {
      if (secret) {
        this.logger.warn("Email delivery failed for a secret message");
      } else {
        this.logger.warn("Email delivery failed", {
          to: input.recipient,
          subject: input.subject,
          error: getErrorMessage(error),
        });
      }
      return {
        status: "failed" as const,
        failureCode: "email_delivery_failed",
      };
    }
  }

  private async sendWithResend(input: {
    to: string;
    subject: string;
    text: string;
    html?: string | undefined;
    threading?: EmailDeliveryThreading | undefined;
    idempotencyKey?: string | undefined;
  }): Promise<EmailSendResult> {
    const apiKey = this.config.apiKey;
    const from = this.config.from;
    if (!apiKey || !from) {
      return { status: "failed", code: "email_not_configured" };
    }

    const response = await this.fetchImpl("https://api.resend.com/emails", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
        ...(input.idempotencyKey
          ? { "Idempotency-Key": input.idempotencyKey }
          : {}),
      },
      body: JSON.stringify({
        from,
        to: input.to,
        subject: input.subject,
        text: input.text,
        ...(input.html ? { html: input.html } : {}),
        ...(input.threading
          ? {
              headers: {
                "In-Reply-To": input.threading.inReplyTo,
                References: input.threading.references.join(" "),
              },
            }
          : {}),
      }),
    });

    if (!response.ok) {
      return { status: "failed", code: await resendFailureCode(response) };
    }

    const body = resendEmailResponseSchema.parse(await response.json());
    return body.id ? { status: "sent", id: body.id } : { status: "sent" };
  }
}

export function emailInterface(config: EmailConfigInput = {}): EmailInterface {
  return new EmailInterface(config);
}
